import React, { useMemo } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../components/ui/select';
import { FileText, Zap, Settings, ChevronDown, ChevronUp, X, User, Hash } from 'lucide-react';

export function TarefaFormDialog({
  open, onOpenChange, editingTarefa, formData, setFormData,
  categoriasTarefas, operacoesSugeridas, operacoesDisponiveis,
  operadoresFiltrados, showOperacoesSelector, setShowOperacoesSelector,
  onSave, onToggleSugerida, onAddOperacao, onRemoveOperacao, onOpenConfig
}) {
  // Operações sugeridas: priorizar as da categoria selecionada, fallback para globais
  const operacoesSugeridasEfetivas = useMemo(() => {
    if (!formData.categoria_id) return []; // Sem categoria = sem sugestões
    const cat = categoriasTarefas.find(c => c.id === formData.categoria_id);
    if (cat?.operacoes_sugeridas?.length > 0) {
      return cat.operacoes_sugeridas;
    }
    return operacoesSugeridas;
  }, [formData.categoria_id, categoriasTarefas, operacoesSugeridas]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {editingTarefa ? 'Editar Tarefa' : 'Nova Tarefa'}
          </DialogTitle>
          <DialogDescription>
            {editingTarefa ? 'Modifique os detalhes da tarefa' : 'Crie uma nova tarefa para a bolsa'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Categoria</label>
              <Select
                value={formData.categoria_id || "none"}
                onValueChange={(v) => setFormData(prev => ({ ...prev, categoria_id: v === "none" ? '' : v }))}
              >
                <SelectTrigger data-testid="tarefa-categoria-select">
                  <SelectValue placeholder="Selecionar categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem categoria</SelectItem>
                  {categoriasTarefas.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.cor }} />
                        {cat.nome}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="flex flex-col gap-2 pb-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="urgente"
                    checked={formData.urgente}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, urgente: checked }))}
                    data-testid="tarefa-urgente-checkbox"
                  />
                  <label htmlFor="urgente" className="text-sm font-medium text-stone-700 flex items-center gap-1">
                    <Zap className="h-4 w-4 text-red-500" />Tarefa Urgente
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="obriga_contagem"
                    checked={formData.obriga_contagem || false}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, obriga_contagem: !!checked }))}
                    data-testid="tarefa-obriga-contagem-checkbox"
                  />
                  <label htmlFor="obriga_contagem" className="text-sm font-medium text-stone-700 flex items-center gap-1">
                    <Hash className="h-4 w-4 text-blue-500" />Obriga Contagem
                    {formData.obriga_contagem && (formData.operacoes || []).some(o => {
                      const cat = operacoesDisponiveis.find(od => od.id === o.operacao_id);
                      return cat?.is_contagem === true;
                    }) && (
                      <span className="text-[10px] font-normal italic text-blue-500" title="Auto-marcado por operação de contagem">(auto)</span>
                    )}
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Notas (opcional) */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Notas <span className="text-stone-400 font-normal">(opcional)</span></label>
            <Input
              placeholder="Ex: Verificar lote 123, atenção ao encolhimento..."
              value={formData.observacoes || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
              data-testid="tarefa-notas-input"
            />
          </div>

          {/* Operacoes */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Operacoes *</label>
            {operacoesSugeridasEfetivas.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-amber-700">
                  Operacoes Sugeridas (da categoria):
                </p>
                <button type="button" onClick={onOpenConfig}
                  className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1" title="Configurar operacoes sugeridas">
                  <Settings className="h-3 w-3" />Configurar
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {operacoesSugeridasEfetivas.map((op) => {
                  const selecionada = formData.operacoes.some(o => o.operacao_id === op.operacao_id);
                  return (
                    <button key={op.operacao_id} type="button" onClick={() => onToggleSugerida(op)}
                      className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                        selecionada ? 'bg-primary text-white border-primary' : 'bg-white text-stone-600 border-stone-200 hover:border-primary hover:text-primary'
                      }`}>
                      <span className="font-mono mr-1">{op.operacao_codigo}</span>{op.operacao_nome}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {formData.operacoes.length > 0 && (
              <div className="space-y-2 mb-3">
                {formData.operacoes.map((op, idx) => (
                  <div key={op.operacao_id || idx} className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg border">
                    <span className="font-mono text-xs font-bold px-2 py-0.5 bg-primary/10 text-primary rounded">{op.operacao_codigo}</span>
                    <span className="text-sm flex-1">{op.operacao_nome}</span>
                    <span className="text-xs text-stone-400">{op.tempo_standard}s</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => onRemoveOperacao(idx)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <Button type="button" variant="outline" size="sm"
                onClick={() => setShowOperacoesSelector(!showOperacoesSelector)} className="text-xs">
                {showOperacoesSelector ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                Adicionar outras operacoes
              </Button>
              {showOperacoesSelector && (
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {(() => {
                    const ops = operacoesDisponiveis
                      .filter(op => !formData.operacoes.some(o => o.operacao_id === op.id))
                      .sort((a, b) => (a.categoria || '').localeCompare(b.categoria || '') || (a.codigo || '').localeCompare(b.codigo || ''));
                    const grupos = {};
                    ops.forEach(op => { const cat = op.categoria || 'Sem Categoria'; if (!grupos[cat]) grupos[cat] = []; grupos[cat].push(op); });
                    return Object.entries(grupos).map(([cat, opsGrupo]) => (
                      <div key={cat}>
                        <p className="text-xs font-bold uppercase text-stone-400 tracking-wider px-2 py-1 bg-stone-50 rounded sticky top-0">{cat}</p>
                        {opsGrupo.map(op => (
                          <button key={op.id} type="button" onClick={() => onAddOperacao(op)}
                            className="w-full flex items-center gap-2 p-2 hover:bg-stone-50 rounded text-left text-sm">
                            <span className="font-mono text-xs text-primary font-bold">{op.codigo}</span>
                            <span className="flex-1 truncate">{op.nome}</span>
                          </button>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Operadores */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Operadores *
              {formData.operacoes.length > 0 && operadoresFiltrados.length === 0 && (
                <span className="text-xs text-amber-600 ml-2">(sem operadores com permissao)</span>
              )}
            </label>
            <div className="flex flex-wrap gap-2 p-2 min-h-[42px] border rounded-md bg-white" data-testid="tarefa-operadores-multiselect">
              {operadoresFiltrados.length === 0 ? (
                <span className="text-sm text-stone-400 py-1">Selecione operacoes primeiro</span>
              ) : (
                operadoresFiltrados.map(op => {
                  const isSelected = formData.operadores_ids.includes(op.id);
                  return (
                    <button key={op.id} type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        operadores_ids: isSelected
                          ? prev.operadores_ids.filter(id => id !== op.id)
                          : [...prev.operadores_ids, op.id]
                      }))}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected ? 'bg-navy text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                      data-testid={`chip-operador-${op.id}`}
                    >
                      <User className="h-3.5 w-3.5" />{op.nome_completo || op.username}
                      {isSelected && <X className="h-3 w-3 ml-0.5" />}
                    </button>
                  );
                })
              )}
            </div>
            {formData.operacoes.length > 0 && (
              <p className="text-[10px] text-stone-400 mt-1">
                {formData.operadores_ids.length > 0
                  ? `${formData.operadores_ids.length} selecionado${formData.operadores_ids.length !== 1 ? 's' : ''} de ${operadoresFiltrados.length}`
                  : `${operadoresFiltrados.length} operador${operadoresFiltrados.length !== 1 ? 'es' : ''} com permissao`}
              </p>
            )}
          </div>

          {editingTarefa && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Estado</label>
              <Select value={formData.estado} onValueChange={(v) => setFormData(prev => ({ ...prev, estado: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Inativa</SelectItem>
                  <SelectItem value="producao">Ativa</SelectItem>
                  <SelectItem value="arquivado">Arquivado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={onSave} data-testid="guardar-tarefa-btn">
              {editingTarefa ? 'Guardar Alteracoes' : 'Criar Tarefa'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
